import { browserFetch } from '../../services/browserSession';
// src/components/reservations/MyReservations.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaCalendarAlt,
  FaBuilding,
      FaClock,
  FaFilter,
  FaInfoCircle,
  FaExclamationTriangle,
    FaTag,
  FaEye,
  FaTimes,
  FaSearch,
  FaCheckCircle,
      FaArrowRight,
    } from 'react-icons/fa';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
// Date picker imports
// import { } from '../common/DatePicker';
import './MyReservations.scss';

// Animation variants following FOTH design system
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  hover: {
    y: -8,
    scale: 1.02,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const MyReservations = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: ''
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Calculate statistics
  const statistics = useMemo(() => {
    const total = reservations.length;
    const confirmed = reservations.filter(r => r.status === 'confirmed').length;
    const pending = reservations.filter(r => r.status === 'pending').length;
    const cancelled = reservations.filter(r => r.status === 'cancelled').length;

    const upcoming = reservations.filter(r =>
      new Date(r.start_datetime) > new Date() && r.status === 'confirmed'
    ).length;

    return {
      total,
      confirmed,
      pending,
      cancelled,
      upcoming
    };
  }, [reservations]);

  // Verificar si el usuario es un creador al cargar el componente
  useEffect(() => {
    if (currentUser && currentUser.user_type !== 'creator') {
      console.log('Usuario no es creador:', currentUser.user_type);
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  const fetchReservations = useCallback(async (newPage = 1) => {
    try {
      setLoading(true);
      setError(null);

      if (currentUser?.user_type !== 'creator') {
        throw new Error('Solo los creadores pueden ver sus reservas');
      }

      const queryParams = new URLSearchParams();
      queryParams.append('page', newPage);

      if (filters.status) queryParams.append('status', filters.status);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);

      const response = await browserFetch(`/api/reservations/?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error('Error al cargar las reservaciones');
      }

      const data = await response.json();
      const results = Array.isArray(data) ? data : (data.results || []);

      if (newPage === 1) {
        setReservations(results);
      } else {
        setReservations(prev => [...prev, ...results]);
      }

      setHasMore(data.next !== null && data.next !== undefined);
      setPage(newPage);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Error al cargar las reservaciones: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, filters]);

  useEffect(() => {
    if (currentUser?.user_type === 'creator') {
      fetchReservations(1);
    }
  }, [fetchReservations, currentUser]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: ''
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), "d 'de' MMMM 'de' yyyy, HH:mm", { locale: es });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-badge pending';
      case 'confirmed': return 'status-badge confirmed';
      case 'cancelled': return 'status-badge cancelled';
      default: return 'status-badge';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'confirmed': return 'Confirmada';
      case 'cancelled': return 'Cancelada';
      default: return status || 'Desconocido';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <FaCheckCircle className="status-icon confirmed" />;
      case 'pending': return <FaExclamationTriangle className="status-icon pending" />;
      case 'cancelled': return <FaTimes className="status-icon cancelled" />;
      default: return <FaClock className="status-icon default" />;
    }
  };

  // Quick action handlers
  const handleBrowseOffers = () => {
    navigate('/creator/offers');
  };

  const handleViewCalendar = () => {
    navigate('/creator/reservations');
  };

  const handleViewDetails = (reservation) => {
    setSelectedReservation(reservation);
  };

  const handleCloseModal = () => {
    setSelectedReservation(null);
  };

  // Si el usuario no está autenticado o está cargando, mostrar spinner
  if (!currentUser) {
    return (
      <div className="my-reservations-container enhanced">
        <motion.div
          className="loading-state"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="loading-spinner" />
          <p>Cargando...</p>
        </motion.div>
      </div>
    );
  }

  // Verificar si el usuario es un creador
  if (currentUser.user_type !== 'creator') {
    return (
      <div className="my-reservations-container enhanced">
        <motion.div
          className="error-state"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <FaExclamationTriangle />
          <h3>Acceso denegado</h3>
          <p>Solo los creadores pueden ver sus reservas</p>
        </motion.div>
      </div>
    );
  }

  if (loading && page === 1) {
    return (
      <div className="my-reservations-container enhanced">
        <motion.div
          className="loading-state"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="loading-spinner" />
          <p>Cargando tus reservaciones...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-reservations-container enhanced">
        <motion.div
          className="error-state"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <FaInfoCircle />
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => fetchReservations(1)} className="btn-retry">
            Intentar de nuevo
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      className="my-reservations-container enhanced"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Enhanced Header */}
      <motion.div className="reservations-header enhanced" variants={itemVariants}>
        <div className="header-content">
          <h1 className="page-title">Mis Reservaciones</h1>
          <p className="page-subtitle">Gestiona y revisa todas tus colaboraciones reservadas</p>
        </div>

        {/* Statistics */}
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-number">{statistics.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{statistics.confirmed}</span>
            <span className="stat-label">Confirmadas</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{statistics.pending}</span>
            <span className="stat-label">Pendientes</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{statistics.upcoming}</span>
            <span className="stat-label">Próximas</span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="header-actions">
          <button className="quick-action-btn primary" onClick={handleBrowseOffers}>
            <FaSearch />
            <span>Explorar Ofertas</span>
          </button>
          <button className="quick-action-btn secondary" onClick={handleViewCalendar}>
            <FaCalendarAlt />
            <span>Ver Calendario</span>
          </button>
          <button
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            <span>Filtros</span>
          </button>
        </div>
      </motion.div>

      {/* Enhanced Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="filters-panel enhanced"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="filters-grid">
              <div className="filter-group">
                <label>
                  <FaTag />
                  <span>Estado</span>
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos los estados</option>
                  <option value="pending">Pendiente</option>
                  <option value="confirmed">Confirmada</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>

              <div className="filter-group">
                <label>
                  <FaCalendarAlt />
                  <span>Desde</span>
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="filter-group">
                <label>
                  <FaCalendarAlt />
                  <span>Hasta</span>
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="filters-actions">
              <button onClick={resetFilters} className="btn-clear">
                <FaTimes />
                Limpiar filtros
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.div className="reservations-content" variants={itemVariants}>
        {reservations.length === 0 ? (
          <motion.div
            className="empty-state enhanced"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="empty-icon">📅</div>
            <h3>No tienes reservaciones aún</h3>
            <p>Explora las ofertas disponibles y comienza a reservar colaboraciones que te interesen.</p>
            <div className="empty-actions">
              <button className="btn-primary" onClick={handleBrowseOffers}>
                <FaSearch />
                Explorar Ofertas
              </button>
              <button className="btn-secondary" onClick={handleViewCalendar}>
                <FaCalendarAlt />
                Ver Calendario
              </button>
            </div>
          </motion.div>
        ) : (
          <>
            <div className="reservations-grid enhanced">
              {reservations.map((reservation, index) => (
                <motion.div
                  key={reservation.id}
                  className={`reservation-card enhanced ${reservation.status}`}
                  variants={cardVariants}
                  whileHover="hover"
                  custom={index}
                  onClick={() => handleViewDetails(reservation)}
                >
                  <div className="card-header">
                    <div className="offer-info">
                      <h3>{reservation.offer_details?.title || 'Oferta sin título'}</h3>
                      <p className="business-name">
                        <FaBuilding />
                        {reservation.offer_details?.business_name || 'Business no especificado'}
                      </p>
                    </div>
                    <div className={getStatusBadgeClass(reservation.status)}>
                      {getStatusIcon(reservation.status)}
                      <span>{getStatusLabel(reservation.status)}</span>
                    </div>
                  </div>

                  {reservation.offer_details?.featured_image && (
                    <div className="card-image">
                      <img
                        src={reservation.offer_details.featured_image}
                        alt={reservation.offer_details.title || 'Imagen de oferta'}
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/400x300/e9ecef/212529?text=Sin+imagen';
                          e.target.onerror = null;
                        }}
                      />
                    </div>
                  )}

                  <div className="card-body">
                    <div className="reservation-details">
                      <div className="detail-item">
                        <FaClock />
                        <div>
                          <strong>Fecha y hora</strong>
                          <p>{format(new Date(reservation.start_datetime), "d 'de' MMM, yyyy", { locale: es })}</p>
                          <span className="time">
                            {format(new Date(reservation.start_datetime), 'HH:mm', { locale: es })} -
                            {format(new Date(reservation.end_datetime), 'HH:mm', { locale: es })}
                          </span>
                        </div>
                      </div>

                      <div className="detail-item">
                        <FaTag />
                        <div>
                          <strong>Tipo de colaboración</strong>
                          <p>{reservation.offer_details?.type_display || 'No especificado'}</p>
                        </div>
                      </div>

                      {reservation.notes && (
                        <div className="detail-item notes">
                          <FaInfoCircle />
                          <div>
                            <strong>Mis notas</strong>
                            <p>{reservation.notes}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="card-footer">
                    <button className="btn-view-details">
                      <FaEye />
                      <span>Ver detalles</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Loading more */}
            {loading && (
              <motion.div
                className="loading-more enhanced"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="loading-spinner" />
                <p>Cargando más reservaciones...</p>
              </motion.div>
            )}

            {/* Load more button */}
            {hasMore && !loading && (
              <motion.div
                className="load-more enhanced"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <button onClick={() => fetchReservations(page + 1)} className="btn-load-more">
                  <FaArrowRight />
                  Cargar más reservaciones
                </button>
              </motion.div>
            )}
          </>
        )}
      </motion.div>

      {/* Reservation Detail Modal */}
      <AnimatePresence>
        {selectedReservation && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className="reservation-modal enhanced"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>{selectedReservation.offer_details?.title || 'Detalles de Reservación'}</h3>
                <button className="btn-close" onClick={handleCloseModal}>
                  <FaTimes />
                </button>
              </div>

              <div className="modal-content">
                <div className="detail-group">
                  <label>Estado</label>
                  <div className={`${getStatusBadgeClass(selectedReservation.status)} large`}>
                    {getStatusIcon(selectedReservation.status)}
                    <span>{getStatusLabel(selectedReservation.status)}</span>
                  </div>
                </div>

                <div className="detail-group">
                  <label>Business</label>
                  <p>{selectedReservation.offer_details?.business_name || 'No especificado'}</p>
                </div>

                <div className="detail-group">
                  <label>Fecha y Hora de Inicio</label>
                  <p>{formatDateTime(selectedReservation.start_datetime)}</p>
                </div>

                <div className="detail-group">
                  <label>Fecha y Hora de Fin</label>
                  <p>{formatDateTime(selectedReservation.end_datetime)}</p>
                </div>

                <div className="detail-group">
                  <label>Tipo de Colaboración</label>
                  <p>{selectedReservation.offer_details?.type_display || 'No especificado'}</p>
                </div>

                {selectedReservation.notes && (
                  <div className="detail-group">
                    <label>Mis Notas</label>
                    <p>{selectedReservation.notes}</p>
                  </div>
                )}

                {selectedReservation.offer_details?.description && (
                  <div className="detail-group">
                    <label>Descripción de la Oferta</label>
                    <p>{selectedReservation.offer_details.description}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MyReservations;
